import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { CardComponent } from '@shared/components/card';
import { ITargetDistributorMonitoring__DistributorDetail } from '../../../target-distributor-monitoring.interface';
import { hasNonZeroAmount } from '../../target-distributor-monitoring.amount';
import { showLevelLabel } from '../../target-distributor-monitoring.level';

/** Nilai `target_program` yang dipakai backend untuk skema target per produk (Target Bundling). */
const TARGET_PER_PRODUCT_LABEL = 'diatur per produk';

/**
 * [MAI-2601] Enam kartu ringkasan di dalam modal Detail per Distributor.
 *
 * Seluruh nilai datang dari backend dalam bentuk string yang sudah terformat
 * ("Rp1.115.200.000,00", "62,5%", "2.000 L") — komponen ini tidak memformat
 * dan tidak mem-parse apa pun, hanya memilih apa yang tampil.
 */
@Component({
  selector: 'app-summary-detail-distributor',
  standalone: true,
  imports: [CommonModule, NgbPopoverModule, CardComponent],
  templateUrl: './summary-detail-distributor.component.html',
  styleUrls: ['./summary-detail-distributor.component.scss'],
})
export class SummaryDetailDistributorComponent implements AfterViewChecked {
  @Input() detail: ITargetDistributorMonitoring__DistributorDetail | null = null;

  /**
   * [MAI-1296] Program hanya punya satu tingkat. Backend mengirim `current_level`
   * "Tingkat 1" untuk program satu tingkat maupun bertingkat, jadi kartu ini tidak bisa
   * membedakannya sendiri. Absen/false = dianggap bertingkat, label tetap tampil.
   */
  @Input() isSingleLevelProgram = false;

  /** [MAI-2604] Link "Lihat Detail" — membuka modal Nilai Invoice. */
  @Output() openInvoiceDetail = new EventEmitter<void>();
  /** [MAI-2725] Link "+N Hadiah Lainnya" — membuka modal Hadiah Didapatkan Distributor. */
  @Output() openOtherRewards = new EventEmitter<void>();

  @ViewChild('rewardText') private rewardTextRef?: ElementRef<HTMLElement>;

  /** Tooltip hanya aktif kalau nama hadiah benar-benar terpotong (AC-03). */
  isRewardTruncated = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /**
   * [AC-02] Baris 2 "Target Program" hanya untuk program multi tingkat — [MAI-1296]
   * pembedanya `isSingleLevelProgram` dari payload modal, bukan isi `current_level`.
   */
  get hasLevel(): boolean {
    return showLevelLabel(this.detail?.current_level, this.isSingleLevelProgram);
  }

  /**
   * [AC-02] Skema Per Produk hanya menampilkan persentase, tanpa rasio realisasi/target.
   *
   * Kontrak backend tidak punya penanda skema — satu-satunya sinyal adalah isi string
   * `target_program` yang berbunyi "Diatur per produk". Kalau nanti backend menambah
   * flag eksplisit, ganti pengecekan ini.
   */
  get showProgressRatio(): boolean {
    const target = this.detail?.target_program?.toLowerCase() ?? '';
    return !target.includes(TARGET_PER_PRODUCT_LABEL);
  }

  /**
   * [MAI-2725] Hanya nama hadiah yang dipotong ellipsis; keterangan kelipatan
   * "(Didapatkan Nx)" dirender span sendiri dengan style Body 2 abu-abu, sesuai design
   * (Figma 8235-51532) — jadi keterangannya tidak ikut terpotong saat nama panjang.
   */
  get rewardName(): string {
    return this.detail?.distributor_reward || '-';
  }

  /**
   * [MAI-2601 QA] Link "Lihat Detail" disembunyikan saat total invoice masih nol —
   * modal Nilai Invoice tidak punya isi apa pun untuk ditampilkan.
   */
  get hasInvoiceDetail(): boolean {
    return hasNonZeroAmount(this.detail?.total_invoice);
  }

  /**
   * [MAI-2601 QA] Kartu Estimasi Budget Hadiah dan %Budget Hadiah menampilkan "-" saat
   * distributor belum mendapat hadiah — backend mengirim "Rp 0,00"/"0%", bukan null,
   * jadi nilai nol harus dipetakan sendiri. Aturannya sama dengan kolom Estimasi Budget
   * Hadiah di tabel monitoring.
   */
  protected amountOrDash(value: string | null | undefined): string {
    return hasNonZeroAmount(value) ? value! : '-';
  }

  get hasOtherRewards(): boolean {
    return (this.detail?.other_reward_count ?? 0) > 0;
  }

  ngAfterViewChecked(): void {
    const element = this.rewardTextRef?.nativeElement;
    if (!element) return;

    const truncated = element.scrollWidth > element.clientWidth;
    if (truncated === this.isRewardTruncated) return;

    this.isRewardTruncated = truncated;
    this.cdr.detectChanges();
  }
}
